import moment from 'moment';

/**
 * Centralized Time Synchronization Service
 *
 * This service manages client-server time synchronization to ensure accurate
 * job fair status calculations regardless of client clock drift.
 *
 * Architecture:
 * 1. WebSocket receives server timestamp (Unix seconds) with each message
 * 2. TimeService calculates and stores the time delta (server - client)
 * 3. All components use getCurrentTime() for synchronized time
 *
 * Units: All timestamps are in Unix seconds unless explicitly noted
 */

const STORAGE_KEYS = {
  SERVER_TIMESTAMP: 'serverTimestamp', // Last received server time (Unix seconds)
  TIME_DELTA: 'timeDelta', // Cached delta in seconds (server - client)
  LAST_SYNC: 'lastTimeSync' // Timestamp of last sync (Unix seconds)
} as const;

const SYNC_CONFIG = {
  MAX_DELTA_AGE_SECONDS: 300, // Recalculate delta if older than 5 minutes
  REASONABLE_DELTA_THRESHOLD: 86400 * 2 // Reject deltas > 2 days (likely error)
} as const;

export class TimeService {
  /**
   * Update the server timestamp received from WebSocket
   * This should be called whenever a new timestamp is received
   *
   * @param serverTimestamp - Unix timestamp in seconds from server
   */
  updateServerTime (serverTimestamp: number): void {
    if (!this.isReasonableTimestamp(serverTimestamp)) {
      console.warn(`[TimeService] Received unreasonable server timestamp: ${serverTimestamp}`);
      return;
    }

    const currentClientTime = this.getCurrentClientTime();
    const delta = serverTimestamp - currentClientTime;

    // Validate delta is within reasonable bounds
    if (Math.abs(delta) > SYNC_CONFIG.REASONABLE_DELTA_THRESHOLD) {
      console.warn(`[TimeService] Time delta too large (${delta}s), ignoring sync`);
      return;
    }

    localStorage.setItem(STORAGE_KEYS.SERVER_TIMESTAMP, serverTimestamp.toString());
    localStorage.setItem(STORAGE_KEYS.TIME_DELTA, delta.toString());
    localStorage.setItem(STORAGE_KEYS.LAST_SYNC, currentClientTime.toString());

    console.log(`[TimeService] Time synced - Delta: ${delta}s (${Math.abs(delta) < 60 ? 'acceptable' : 'significant drift'})`);
  }

  /**
   * Get current synchronized time
   * Returns server time if available, falls back to client time
   *
   * @returns Unix timestamp in seconds
   */
  getCurrentTime (): number {
    const delta = this.getTimeDelta();
    const clientTime = this.getCurrentClientTime();

    if (delta !== null) {
      const syncedTime = clientTime + delta;
      return syncedTime;
    }

    // Fallback to client time if no server sync available
    console.warn('[TimeService] No server time sync available, using client time');
    return clientTime;
  }

  /**
   * Get current time as moment object (UTC)
   *
   * @returns moment object in UTC
   */
  getCurrentMoment (): moment.Moment {
    return moment.unix(this.getCurrentTime()).utc();
  }

  /**
   * Get the time delta (server - client) in seconds
   * Returns null if delta is unavailable or stale
   *
   * @returns Delta in seconds or null
   */
  private getTimeDelta (): number | null {
    const deltaStr = localStorage.getItem(STORAGE_KEYS.TIME_DELTA);
    const lastSyncStr = localStorage.getItem(STORAGE_KEYS.LAST_SYNC);

    if (!deltaStr || !lastSyncStr) {
      return null;
    }

    const delta = parseFloat(deltaStr);
    const lastSync = parseFloat(lastSyncStr);
    const currentClientTime = this.getCurrentClientTime();
    const deltaAge = currentClientTime - lastSync;

    // Delta is too old, consider it stale
    if (deltaAge > SYNC_CONFIG.MAX_DELTA_AGE_SECONDS) {
      console.warn(`[TimeService] Time delta is stale (${deltaAge}s old), ignoring`);
      return null;
    }

    return delta;
  }

  /**
   * Get current client time (browser time)
   *
   * @returns Unix timestamp in seconds
   */
  private getCurrentClientTime (): number {
    return moment().unix();
  }

  /**
   * Check if a timestamp is reasonable (within +/- 5 years of client time)
   * This helps catch obvious errors in server timestamps
   *
   * @param timestamp - Unix timestamp in seconds
   * @returns true if timestamp seems reasonable
   */
  private isReasonableTimestamp (timestamp: number): boolean {
    const clientTime = this.getCurrentClientTime();
    const fiveYearsInSeconds = 86400 * 365 * 5;

    return Math.abs(timestamp - clientTime) < fiveYearsInSeconds;
  }

  /**
   * Get sync status information for debugging
   *
   * @returns Object with sync status details
   */
  getSyncStatus (): {
    hasSyncedTime: boolean;
    delta: number | null;
    lastSync: number | null;
    deltaAge: number | null;
    isStale: boolean;
    } {
    const deltaStr = localStorage.getItem(STORAGE_KEYS.TIME_DELTA);
    const lastSyncStr = localStorage.getItem(STORAGE_KEYS.LAST_SYNC);

    const delta = deltaStr ? parseFloat(deltaStr) : null;
    const lastSync = lastSyncStr ? parseFloat(lastSyncStr) : null;
    const deltaAge = lastSync ? (this.getCurrentClientTime() - lastSync) : null;
    const isStale = deltaAge ? deltaAge > SYNC_CONFIG.MAX_DELTA_AGE_SECONDS : true;

    return {
      hasSyncedTime: delta !== null && !isStale,
      delta,
      lastSync,
      deltaAge,
      isStale
    };
  }

  /**
   * Clear all time sync data (useful for debugging)
   */
  clearSyncData (): void {
    Object.values(STORAGE_KEYS).forEach(key => localStorage.removeItem(key));
    console.log('[TimeService] Time sync data cleared');
  }

  // ============================================================================
  // DEPRECATED METHODS - Kept for backward compatibility, will be removed
  // ============================================================================

  /**
   * @deprecated Use getCurrentTime() instead
   */
  async getServerTime (): Promise<number | null> {
    console.warn('[TimeService] getServerTime() is deprecated, use getCurrentTime() instead');
    const delta = this.getTimeDelta();
    if (delta === null) {
      return null;
    }
    return this.getCurrentTime();
  }

  /**
   * @deprecated This is handled automatically by updateServerTime()
   */
  async loadDelta (): Promise<string | null> {
    console.warn('[TimeService] loadDelta() is deprecated and no longer needed');
    return localStorage.getItem(STORAGE_KEYS.TIME_DELTA);
  }
}

// Export singleton instance
export const timeService = new TimeService();
